/**
 * React hook that manages the lifecycle of a handoff room connection.
 *
 * The hook is responsible for:
 *  - Creating/destroying the connection when the token changes.
 *  - Subscribing to state and mute transitions and triggering re-renders.
 *  - Calling connect() automatically when a token is provided.
 *  - Exposing imperative controls (mute, unmute, leave) directly on the result.
 *
 * It intentionally does NOT hold a provider API key, does NOT call the backend
 * itself, and does NOT know how the token was obtained. The parent component
 * fetches the token from the backend after permission checks and passes it here.
 *
 * In test/Storybook environments pass `createRoom` from `@hostesa/livekit-client/mock`
 * instead of the default factory so no real SDK is instantiated.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createHandoffRoom } from '../handoff-room.js';
import type { HandoffRoomConnection, HandoffRoomState, HandoffToken } from '../types.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RoomFactory = (options: HandoffToken) => HandoffRoomConnection;

export interface UseHandoffRoomResult {
  /** Canonical lifecycle state. */
  state: HandoffRoomState;
  /** True when the local microphone is muted. */
  muted: boolean;
  /** Mute the local microphone. */
  mute: () => void;
  /** Un-mute the local microphone. */
  unmute: () => void;
  /** Leave the room immediately. */
  leave: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param token - Opaque token from the backend token endpoint. Pass undefined/null
 *   to remain idle (no connection attempt made).
 * @param createRoom - Optional factory override for testing / mock scenarios.
 *   Defaults to the real `createHandoffRoom` factory.
 */
export function useHandoffRoom(
  token: HandoffToken | null | undefined,
  createRoom: RoomFactory = createHandoffRoom,
): UseHandoffRoomResult {
  const [state, setState] = useState<HandoffRoomState>('idle');
  const [muted, setMuted] = useState(false);

  // Keep a stable ref to the active connection so callbacks don't capture stale closures.
  const connRef = useRef<HandoffRoomConnection | null>(null);

  // Hold the latest createRoom factory in a ref so that changing the factory
  // reference (e.g. an inline arrow in render) does NOT restart the effect.
  // The effect intentionally depends only on token identity.
  const createRoomRef = useRef<RoomFactory>(createRoom);
  createRoomRef.current = createRoom;

  useEffect(() => {
    if (!token) {
      // No token — stay idle; clean up any previous connection.
      if (connRef.current) {
        connRef.current.destroy();
        connRef.current = null;
      }
      setState('idle');
      setMuted(false);
      return;
    }

    // Build connection and subscribe before calling connect() to avoid missing
    // the very first state transition.
    const conn = createRoomRef.current(token);
    connRef.current = conn;

    const unsubState = conn.onStateChange((s) => {
      setState(s);
    });
    const unsubMute = conn.onMuteChange((m) => {
      setMuted(m);
    });

    // Sync initial values
    setState(conn.state);
    setMuted(conn.muted);

    // Fire-and-forget; errors are surfaced via state transitions inside the controller.
    void conn.connect();

    return () => {
      unsubState();
      unsubMute();
      conn.destroy();
      connRef.current = null;
    };
    // Intentionally depend only on token identity (object ref) — a new token
    // object means the parent decided to reconnect.
    // createRoom is deliberately excluded: it is read via createRoomRef so that
    // an unstable factory reference (e.g. an inline arrow in JSX) does not
    // teardown and recreate the connection on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const mute = useCallback(() => {
    connRef.current?.mute();
  }, []);

  const unmute = useCallback(() => {
    connRef.current?.unmute();
  }, []);

  const leave = useCallback(() => {
    connRef.current?.leave();
  }, []);

  return { state, muted, mute, unmute, leave };
}
